<template>
  <b-modal
    v-model="show"
    size="xl"
    :title="modalTitle"
    hide-footer
    no-close-on-backdrop
    scrollable
    modal-class="rto-seq-modal"
    @hidden="onHidden"
  >
    <div class="rto-seq">
      <!-- Header status -->
      <header class="rto-seq-header">
        <div class="rto-seq-status">
          <span class="rto-seq-chip" :class="statusChipClass">
            <b-spinner v-if="connecting" small class="mr-1" />
            <b-icon v-else-if="connected" icon="broadcast" class="mr-1" />
            <b-icon v-else icon="x-circle" class="mr-1" />
            {{ statusLabel }}
          </span>
          <span class="rto-seq-counter" v-if="iteracoes.length > 0">
            {{ iteracoes.length }} iterações recebidas
          </span>
        </div>
        <div class="rto-seq-meta" v-if="latestIteracao">
          <div>
            <label>Violações</label>
            <span class="mono" :class="latestIteracao.violacoes > 0 ? 'rto-seq-atencao' : 'rto-seq-ok'">
              {{ latestIteracao.violacoes }}
            </span>
          </div>
          <div>
            <label>Iteração</label>
            <span class="mono">{{ latestIteracao.iteracao }}</span>
          </div>
        </div>
      </header>

      <!-- Error -->
      <div v-if="error" class="rto-seq-error">
        <b-icon icon="exclamation-triangle-fill" class="mr-1" /> {{ error }}
      </div>

      <!-- Otimização finalizada -->
      <div v-if="finalizado" class="rto-seq-finalizado" :class="finalizadoClass">
        <b-icon :icon="finalizadoIcon" class="mr-2" />
        <span>{{ finalizadoLabel }}</span>
      </div>

      <!-- Chart -->
      <section v-if="iteracoes.length > 0" class="rto-seq-section">
        <h4 class="rto-seq-section-title">Variável objetivo ao longo das iterações</h4>
        <apexchart
          type="line"
          height="360"
          :options="chartOptions"
          :series="chartSeries"
        />
      </section>

      <!-- Latest inputs/outputs -->
      <section v-if="latestIteracao" class="rto-seq-section">
        <h4 class="rto-seq-section-title">
          Manipuladas (inputs)
          <span class="rto-seq-section-count">{{ Object.keys(latestIteracao.inputs || {}).length }}</span>
        </h4>
        <table class="rto-seq-table">
          <thead>
            <tr>
              <th>Tag</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(value, tag) in latestIteracao.inputs" :key="'in-' + tag">
              <td class="mono" :title="tag">{{ tagToLabel[tag] || tag }}</td>
              <td class="mono text-right">{{ formatNum(value) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section v-if="latestIteracao" class="rto-seq-section">
        <h4 class="rto-seq-section-title">
          Outputs (Objetivo + Restrições)
          <span class="rto-seq-section-count">{{ Object.keys(latestIteracao.outputs || {}).length }}</span>
          <span v-if="latestIteracao.violacoes > 0" class="rto-seq-section-tag rto-seq-section-tag--atencao">
            <b-icon icon="exclamation-triangle-fill" class="mr-1" />{{ latestIteracao.violacoes }} em atenção
          </span>
        </h4>
        <table class="rto-seq-table">
          <thead>
            <tr>
              <th>Tag</th>
              <th class="text-right">Valor Simulado</th>
              <th class="text-right">Limite (mín … máx)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(out, tag) in latestIteracao.outputs"
              :key="'out-' + tag"
              :class="{ 'rto-seq-row-violado': outViolado(out) }"
            >
              <td class="mono" :title="tag">
                <b-icon v-if="outViolado(out)" icon="exclamation-triangle-fill" class="rto-seq-row-icon mr-1" />
                {{ tagToLabel[tag] || tag }}
              </td>
              <td class="mono text-right" :class="{ 'rto-seq-atencao': outViolado(out) }">
                {{ formatNum(outValor(out)) }}
              </td>
              <td class="mono text-right rto-seq-limite">{{ outLimite(out) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Waiting state -->
      <div v-if="iteracoes.length === 0 && connected" class="rto-seq-empty">
        <b-spinner small class="mr-2" />
        Conectado. Aguardando primeira iteração...
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';
import * as signalR from '@microsoft/signalr';
import { urlMainAPI } from '@/global';
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'RtoSeqMonitor',

  components: { apexchart: VueApexCharts },

  props: {
    visible: { type: Boolean, default: false },
    processamentoId: { type: Number, default: null },
    otimizacaoNome: { type: String, default: '' },
    tagToLabel: { type: Object, default: () => ({}) },
  },

  emits: ['update:visible', 'close', 'iteracao', 'finalizado'],

  data() {
    return {
      show: false,
      connection: null,
      iteracoes: [],
      connecting: false,
      connected: false,
      error: null,
      workerUrl: null,
      lastConnectedProcId: null,
      // Preenchido pelo evento "finalizado" do Hub quando a otimização encerra
      finalizado: null,
    };
  },

  computed: {
    modalTitle() {
      if (!this.processamentoId) return 'SEQ — Monitor em Tempo Real';
      const name = this.otimizacaoNome ? ` — ${this.otimizacaoNome}` : '';
      return `Otimização ${this.processamentoId}${name}`;
    },

    latestIteracao() {
      return this.iteracoes.length > 0 ? this.iteracoes[this.iteracoes.length - 1] : null;
    },

    statusLabel() {
      if (this.finalizado) return 'Processo encerrado';
      if (this.connecting) return 'Conectando...';
      if (this.connected) return 'Conectado';
      if (this.error) return 'Erro de conexão';
      return 'Desconectado';
    },

    statusChipClass() {
      if (this.finalizado) return 'rto-seq-chip--neutro';
      if (this.connecting) return 'rto-seq-chip--pendente';
      if (this.connected) return 'rto-seq-chip--ok';
      return 'rto-seq-chip--neutro';
    },

    finalizadoClass() {
      if (!this.finalizado) return '';
      if (this.finalizado.sucesso) return 'rto-seq-finalizado--ok';
      if (this.finalizado.cancelado) return 'rto-seq-finalizado--cancel';
      return 'rto-seq-finalizado--erro';
    },

    finalizadoIcon() {
      if (!this.finalizado) return '';
      if (this.finalizado.sucesso) return 'check-circle-fill';
      if (this.finalizado.cancelado) return 'slash-circle-fill';
      return 'exclamation-triangle-fill';
    },

    finalizadoLabel() {
      if (!this.finalizado) return '';
      if (this.finalizado.sucesso) return 'Otimização concluída com sucesso. O processo terminou.';
      if (this.finalizado.cancelado) return 'Otimização cancelada. O processo terminou.';
      const motivo = this.finalizado.resultado ? ` — ${this.finalizado.resultado}` : '';
      return `Otimização finalizada com erro${motivo}`;
    },

    chartSeries() {
      // Apenas a variável objetivo. O objetivo é sempre o primeiro output
      // (outputMapping[0]); os demais outputs são restrições (Worker usa i >= 1).
      // Manter só o objetivo evita o problema de escala (variáveis de magnitudes
      // diferentes achatadas num eixo Y compartilhado).
      let objTag = null;
      for (const it of this.iteracoes) {
        const outTags = Object.keys(it.outputs || {});
        if (outTags.length) { objTag = outTags[0]; break; }
      }
      if (!objTag) return [];
      return [{
        name: objTag,
        data: this.iteracoes.map(it => {
          const raw = this.outValor(it.outputs?.[objTag]);
          return { x: it.iteracao, y: raw != null && !isNaN(Number(raw)) ? Number(raw) : null };
        }),
      }];
    },

    chartOptions() {
      return {
        chart: {
          id: 'rto-seq-vars',
          toolbar: { show: false },
          animations: { enabled: true, easing: 'linear', dynamicAnimation: { speed: 300 } },
          fontFamily: 'inherit',
        },
        stroke: { curve: 'straight', width: 1.5 },
        xaxis: {
          title: { text: 'Iteração' },
          type: 'numeric',
        },
        yaxis: {
          title: { text: 'Valor' },
          labels: { formatter: v => this.formatNum(v) },
        },
        markers: { size: 2 },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '11px',
          // ApexCharts: clicar na legenda alterna a visibilidade da série
          // (equivale aos checkboxes da sidebar do "Acompanhar Otimização").
        },
        grid: { borderColor: '#e5e7eb' },
        tooltip: {
          shared: true,
          intersect: false,
          x: { formatter: v => `Iteração ${v}` },
          y: { formatter: v => this.formatNum(v) },
        },
      };
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(v) {
        this.show = v;
        if (v) this.start();
        // Não para a conexão ao esconder — preserva iteracoes acumuladas
      },
    },
    show(v) {
      if (v !== this.visible) this.$emit('update:visible', v);
    },
    processamentoId(newId, oldId) {
      if (newId !== oldId) {
        // Mudou de otimização — fecha a anterior e começa do zero
        this.stop().then(() => {
          this.iteracoes = [];
          this.finalizado = null;
          this.lastConnectedProcId = null;
          if (this.show) this.start();
        });
      }
    },
  },

  beforeDestroy() {
    this.stop();
  },

  methods: {
    async start() {
      if (!this.processamentoId) return;
      // Reusa conexão existente se já estamos conectados no mesmo processamento
      if (this.connected && this.lastConnectedProcId === this.processamentoId) return;
      this.error = null;
      this.connecting = true;
      this.connected = false;
      try {
        if (!this.workerUrl) await this.loadWorkerUrl();
        if (!this.workerUrl) {
          this.error = 'URL do Worker RTO não configurada (cnsettings.json: urlWorkerRTO).';
          this.connecting = false;
          return;
        }
        const hubUrl = `${this.workerUrl.replace(/\/$/, '')}/hubs/otimizacao`;
        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl)
          .withAutomaticReconnect()
          .build();

        this.connection.on('iteracao', this.onIteracao);
        this.connection.on('finalizado', this.onFinalizado);
        this.connection.onreconnected(async () => {
          this.connected = true;
          this.error = null;
          await this.safeSubscribe();
        });
        this.connection.onclose(() => {
          this.connected = false;
          this.connecting = false;
        });

        await this.connection.start();
        const subscribed = await this.safeSubscribe();
        if (!subscribed) {
          throw new Error('Conexão estabelecida mas falhou ao se inscrever no processamento.');
        }
        this.connected = true;
        this.lastConnectedProcId = this.processamentoId;
      } catch (e) {
        console.error('Erro ao conectar no SignalR:', e);
        this.error = `Falha ao conectar: ${e?.message ?? e}`;
        this.connected = false;
      } finally {
        this.connecting = false;
      }
    },

    async safeSubscribe() {
      // Tenta fazer Subscribe garantindo que a conexão está em Connected.
      // Se ainda estiver em Connecting, aguarda até 1s antes de desistir.
      if (!this.connection || !this.processamentoId) return false;
      const deadline = Date.now() + 1000;
      while (this.connection.state !== signalR.HubConnectionState.Connected) {
        if (Date.now() > deadline) return false;
        if (this.connection.state === signalR.HubConnectionState.Disconnected) return false;
        await new Promise(r => setTimeout(r, 50));
      }
      try {
        await this.connection.invoke('Subscribe', this.processamentoId);
        return true;
      } catch (e) {
        console.warn('Falha ao Subscribe (ignorada se reconnect em andamento):', e?.message ?? e);
        return false;
      }
    },

    async stop() {
      if (!this.connection) return;
      try {
        if (this.connected && this.processamentoId) {
          try { await this.connection.invoke('Unsubscribe', this.processamentoId); } catch { /* conexão já encerrada */ }
        }
        await this.connection.stop();
      } catch (e) {
        console.warn('Erro ao parar conexão:', e);
      } finally {
        this.connection = null;
        this.connected = false;
        this.connecting = false;
      }
    },

    async loadWorkerUrl() {
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/worker-url`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        this.workerUrl = data?.url || null;
      } catch (e) {
        console.error('Erro ao buscar URL do Worker:', e);
        this.workerUrl = null;
      }
    },

    onIteracao(payload) {
      this.iteracoes.push(payload);
      this.$emit('iteracao', payload);
    },

    onFinalizado(payload) {
      this.finalizado = payload;
      this.$emit('finalizado', payload);
      // Otimização encerrada — o worker não emite mais nada. Encerra a conexão SignalR.
      this.stop();
    },

    onHidden() {
      this.$emit('close');
    },

    formatNum(val) {
      if (val == null || isNaN(Number(val))) return '—';
      return Number(val).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
    },

    // outputs do payload: { valor, min, max, violado }. Tolera payload antigo (número cru).
    outValor(out) {
      if (out == null) return null;
      return typeof out === 'object' ? out.valor : out;
    },

    outViolado(out) {
      return typeof out === 'object' && out !== null && out.violado === true;
    },

    outLimite(out) {
      if (typeof out !== 'object' || out === null) return '—';
      if (out.min == null && out.max == null) return '—';
      const min = out.min != null ? this.formatNum(out.min) : '−∞';
      const max = out.max != null ? this.formatNum(out.max) : '+∞';
      return `${min} … ${max}`;
    },
  },
};
</script>

<style scoped>
.rto-seq {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rto-seq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
}

.rto-seq-status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rto-seq-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rto-seq-chip--ok { background-color: #dcfce7; color: #15803d; }
.rto-seq-chip--pendente { background-color: #fef3c7; color: #92400e; }
.rto-seq-chip--neutro { background-color: #e5e7eb; color: #6b7280; }

.rto-seq-counter {
  font-size: 0.72rem;
  color: #6b7280;
}

.rto-seq-meta {
  display: flex;
  gap: 18px;
}

.rto-seq-meta > div {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.rto-seq-meta label {
  font-size: 0.6rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.rto-seq-meta span {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
}

.rto-seq-ok { color: #15803d !important; }
.rto-seq-danger { color: #b91c1c !important; }
.rto-seq-atencao { color: #b45309 !important; }

.rto-seq-error {
  padding: 10px 12px;
  background-color: #fee2e2;
  color: #b91c1c;
  border-radius: 4px;
  font-size: 0.78rem;
}

.rto-seq-finalizado {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.rto-seq-finalizado--ok {
  background-color: #dcfce7;
  color: #15803d;
  border-color: #86efac;
}

.rto-seq-finalizado--cancel {
  background-color: #e5e7eb;
  color: #6b7280;
  border-color: #d1d5db;
}

.rto-seq-finalizado--erro {
  background-color: #fee2e2;
  color: #b91c1c;
  border-color: #fca5a5;
}

.rto-seq-section {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
}

.rto-seq-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0 0 8px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.rto-seq-section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  background-color: #6b7280;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  border-radius: 9px;
}

.rto-seq-section-tag {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
}

.rto-seq-section-tag--atencao { background-color: #fef3c7; color: #92400e; }

.rto-seq-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.rto-seq-table thead th {
  background-color: #f3f4f6;
  color: #6b7280;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.rto-seq-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 100ms;
}

.rto-seq-table tbody tr:hover {
  background-color: #fff7ed;
}

/* Restrição em atenção — fundo levemente âmbar */
.rto-seq-table tbody tr.rto-seq-row-violado {
  background-color: #fffbeb;
}

.rto-seq-table tbody tr.rto-seq-row-violado:hover {
  background-color: #fef3c7;
}

.rto-seq-row-icon {
  color: #f59e0b;
}

.rto-seq-limite {
  color: #9ca3af;
}

.rto-seq-table tbody td {
  padding: 6px 10px;
  color: #374151;
}

.mono {
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  font-size: 0.72rem;
}

.text-right { text-align: right; }

.rto-seq-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 0.85rem;
}
</style>
